import React from "react";
import {
  Bot,
  Coins,
  Database,
  HelpCircle,
  Image as ImageIcon,
  Layers,
  Plug,
  Search,
  Workflow,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { formatCoins, toNumber } from "./format";
import { cn } from "../../lib/utils";

/**
 * Human labels and icons for the backend's `resource_type` values.
 *
 * Unknown types are rendered as-is (title-cased) rather than dropped — a resource
 * type this map has not caught up with is still real spend, and silently hiding it
 * would put the bars out of sync with the total.
 */
const RESOURCE_META = {
  WORKFLOW_STEP: { label: "Workflow steps", Icon: Layers },
  WORKFLOW_EXECUTION: { label: "Workflow runs", Icon: Workflow },
  ASSISTANT_CALL: { label: "Agent calls", Icon: Bot },
  INTEGRATION_CALL: { label: "Integration calls", Icon: Plug },
  IMAGE_GENERATION: { label: "Image generation", Icon: ImageIcon },
  RAG_QUERY: { label: "Knowledge queries", Icon: Search },
  DATABASE_READ: { label: "Database reads", Icon: Database },
  DATABASE_WRITE: { label: "Database writes", Icon: Database },
};

function humanize(resourceType) {
  const known = RESOURCE_META[resourceType];
  if (known) return known;
  const label = String(resourceType || "Other")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
  return { label, Icon: Coins };
}

function ResourceRow({ resourceType, coins, sharePct, isUnattributed = false }) {
  const { label, Icon } = isUnattributed
    ? { label: "No project", Icon: HelpCircle }
    : humanize(resourceType);
  const width = Math.max(2, Math.min(100, toNumber(sharePct)));

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "rounded-md border p-1.5",
              isUnattributed
                ? "border-amber-300/20 bg-amber-500/10 text-amber-200"
                : "border-violet-300/20 bg-violet-500/10 text-violet-200"
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-sm text-gray-200">{label}</span>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-sm font-medium tabular-nums text-white">{formatCoins(coins)}</p>
          <p className="text-xs tabular-nums text-gray-400">{toNumber(sharePct).toFixed(1)}%</p>
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className={cn(
            "h-full rounded-full bg-gradient-to-r",
            isUnattributed
              ? "from-amber-400 to-amber-600"
              : "from-violet-500 to-pink-500"
          )}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Where the coins went, by resource type.
 *
 * Replaces the old "Resource Usage" card, which read a rollup table whose writer
 * stopped in May and was account-scoped only — so filtering by project showed
 * account-wide numbers under a project heading.
 *
 * The "No project" bucket is spend that belongs to no project (a database read, an
 * integration call). It is shown as its own row rather than folded into a project's
 * total. **At project scope the backend reports it as 0** and this card hides it:
 * those rows are out of scope, not part of the project's spend.
 */
export function CoinsByResourceCard({
  byResourceType = [],
  unattributedCoins = 0,
  unattributedPct = 0,
  projectScoped = false,
}) {
  const rows = Array.isArray(byResourceType) ? byResourceType : [];
  const showUnattributed = !projectScoped && toNumber(unattributedCoins) > 0;
  const hasData = rows.length > 0 || showUnattributed;

  return (
    <Card className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/85 shadow-[0_16px_50px_rgba(21,20,52,0.5)]">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-xl text-gray-100">
          <Coins className="h-5 w-5 text-amber-300" />
          Spend by resource
        </CardTitle>
        <CardDescription className="text-sm text-gray-300">
          {projectScoped
            ? "What this project's coins were spent on."
            : "What your coins were spent on, across the account."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasData ? (
          <>
            {rows.map((row) => (
              <ResourceRow
                key={row.resource_type}
                resourceType={row.resource_type}
                coins={row.coins}
                sharePct={row.share_pct}
              />
            ))}
            {showUnattributed && (
              <>
                <ResourceRow
                  isUnattributed
                  coins={unattributedCoins}
                  sharePct={unattributedPct}
                />
                <p className="pt-1 text-xs text-gray-500">
                  Spend that isn't tied to a project — database reads, integration
                  calls, coin purchases. Real spend, counted separately so it never
                  inflates a project's total.
                </p>
              </>
            )}
          </>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">
            No coins spent in this period.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** The workflows and agents that cost the most this period. */
export function TopSpendersCard({ topSpenders = [] }) {
  const spenders = (Array.isArray(topSpenders) ? topSpenders : []).filter((s) => s?.id);
  const max = spenders.reduce((best, s) => Math.max(best, toNumber(s.coins)), 0);

  return (
    <Card className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/85 shadow-[0_16px_50px_rgba(21,20,52,0.5)]">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl text-gray-100">Top spenders</CardTitle>
        <CardDescription className="text-sm text-gray-300">
          The workflows and agents that burned the most coins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {spenders.length > 0 ? (
          spenders.map((spender) => {
            const Icon = spender.kind === "agent" ? Bot : Workflow;
            const share = max > 0 ? (toNumber(spender.coins) / max) * 100 : 0;
            return (
              <div key={`${spender.kind}-${spender.id}`} className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="rounded-md border border-violet-300/20 bg-violet-500/10 p-1.5 text-violet-200">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="truncate text-sm text-gray-200">{spender.name}</span>
                    <span className="flex-shrink-0 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gray-400">
                      {spender.kind}
                    </span>
                  </div>
                  <span className="flex-shrink-0 text-sm font-medium tabular-nums text-white">
                    {formatCoins(spender.coins)}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500"
                    style={{ width: `${Math.max(2, share)}%` }}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">
            Nothing spent by a workflow or agent in this period.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
